"use client"

import { useEffect, useRef } from "react"

import { useInfiniteQuery } from "@tanstack/react-query"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"
import { Text } from "@/components/ui/text"
import { loadFeedPage } from "@/features/feed/actions"
import { feedContinuationHref } from "@/features/feed/continuation"
import type { FeedPage, FeedPageType, FeedResponseSection } from "@/features/feed/contract"
import { feedKeys } from "@/features/feed/feed-keys"
import { FeedMoreLink } from "@/features/feed/FeedMoreLink"
import { FeedSections } from "@/features/feed/FeedSections"
import { FeedSectionSkeleton } from "@/features/feed/FeedSectionSkeleton"
import { useBrowseReadiness, useBrowseValue } from "@/platform/app-mode/use-browse-value"
import { useScreenPreparation } from "@/platform/app-mode/use-screen-visible"
import { ANONYMOUS_VIEWER } from "@/platform/session/viewer"

interface FeedScrollerProps {
  pageType: FeedPageType
  handle: string
  page: FeedPage
  countryCode: string
  initialCursor: string
  startIndex: number
  previousSection?: FeedResponseSection
  viewer?: string
}

export const FeedScroller = ({ pageType, handle, page, countryCode, initialCursor, startIndex, previousSection, viewer = ANONYMOUS_VIEWER }: FeedScrollerProps) => {
  const preparing = useScreenPreparation()
  const t = useTranslations("feed")
  const tCommon = useTranslations("common")
  const sentinelRef = useRef<HTMLDivElement>(null)
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError } = useInfiniteQuery({
    queryKey: feedKeys.pages(pageType, handle, initialCursor, viewer),
    queryFn: async ({ pageParam }) => {
      const feedPage = await loadFeedPage(pageType, handle, pageParam)
      if (!feedPage) throw new Error("feed continuation unavailable")
      return feedPage
    },
    initialPageParam: initialCursor,
    getNextPageParam: (lastPage) => (lastPage.paging.hasMore && lastPage.paging.nextCursor ? lastPage.paging.nextCursor : undefined),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    enabled: false,
  })

  const fetched = (data?.pages ?? []).flatMap((feedPage) => feedPage.sections)
  const more = !data || hasNextPage
  const nextCursor = data ? data.pages.at(-1)?.paging.nextCursor : initialCursor
  const failed = isFetchNextPageError && !isFetchingNextPage

  const [rendered, setRendered, entryId] = useBrowseValue(`feed:${pageType}:${handle}:${viewer}`, 0)
  useEffect(() => {
    if (rendered >= fetched.length) return
    const raf = requestAnimationFrame(() => setRendered((count) => Math.min(count + 1, fetched.length)))
    return () => cancelAnimationFrame(raf)
  }, [rendered, fetched.length, setRendered])
  const sections = rendered >= fetched.length ? fetched : fetched.slice(0, rendered)

  useBrowseReadiness(rendered <= fetched.length || !more || isFetchNextPageError, entryId)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!preparing || !sentinel || !more || isFetchingNextPage || failed) return
    const observer = new IntersectionObserver(([entry]) => entry?.isIntersecting && fetchNextPage(), { rootMargin: `${window.innerHeight * 2}px` })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [preparing, more, isFetchingNextPage, failed, fetchNextPage, sections.length])

  useEffect(() => {
    if (preparing && rendered > fetched.length && more && !isFetchingNextPage && !failed) void fetchNextPage()
  }, [preparing, rendered, fetched.length, more, isFetchingNextPage, failed, fetchNextPage])

  return (
    <>
      <FeedSections page={page} sections={sections} countryCode={countryCode} startIndex={startIndex} previousSection={previousSection} />
      {isFetchingNextPage && (
        <div className="mt-flow">
          <FeedSectionSkeleton />
        </div>
      )}
      {failed && (
        <div role="alert" className="px-gut mt-flow flex flex-col items-center gap-300 text-center">
          <Text as="p" variant="body" tone="muted">
            {t("loadError")}
          </Text>
          <Button variant="secondary" size="md" onClick={() => fetchNextPage()}>
            {tCommon("retry")}
          </Button>
        </div>
      )}
      {more && nextCursor && !failed && (
        <FeedMoreLink href={feedContinuationHref({ cursor: nextCursor, startIndex: startIndex + fetched.length })} busy={isFetchingNextPage} onLoad={() => fetchNextPage()} />
      )}
      {more && <div ref={sentinelRef} className="size-px" aria-hidden />}
    </>
  )
}
